// src/components/MyTasks.jsx
import React, { useEffect, useState } from "react";
import api from "../api";
import EmployeeLayout from "./EmployeeLayout";
import { Clock, AlertCircle } from "lucide-react";

export default function MyTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ================= LOAD TASKS ================= */
  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      const res = await api.get("/tasks");
      setTasks(res.data || []);
    } catch (err) {
      console.error("TASK LOAD ERROR:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= HELPERS ================= */
  const groupByStatus = (status) =>
    tasks.filter((task) => task.status === status);

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const statusConfig = {
    todo: {
      label: "Pending",
      color: "border-yellow-400",
      bg: "bg-yellow-50",
    },
    in_progress: {
      label: "In Progress",
      color: "border-blue-400",
      bg: "bg-blue-50",
    },
    review: {
      label: "Review",
      color: "border-purple-400",
      bg: "bg-purple-50",
    },
    done: {
      label: "Completed",
      color: "border-green-400",
      bg: "bg-green-50",
    },
  };

  const priorityColor = {
    low: "bg-slate-200 text-slate-700",
    medium: "bg-amber-200 text-amber-800",
    high: "bg-red-200 text-red-800",
  };

  /* ================= UI ================= */
  return (
    <EmployeeLayout title="My Tasks">
      <div className="max-w-7xl mx-auto p-4">

        {/* Loading */}
        {loading && (
          <p className="text-center text-gray-500">
            Loading your tasks...
          </p>
        )}

        {/* Empty */}
        {!loading && tasks.length === 0 && (
          <div className="text-center text-gray-500 py-16">
            <AlertCircle className="mx-auto mb-3 opacity-40" size={40} />
            No tasks assigned yet.
          </div>
        )}

        {/* Task Board */}
        {!loading && tasks.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {Object.keys(statusConfig).map((status) => {
              const config = statusConfig[status];
              const tasksByStatus = groupByStatus(status);

              return (
                <div
                  key={status}
                  className={`rounded-xl border-t-4 ${config.color} bg-white shadow-sm`}
                >
                  {/* Column Header */}
                  <div
                    className={`px-4 py-3 rounded-t-xl ${config.bg}`}
                  >
                    <h3 className="font-semibold text-sm text-center">
                      {config.label} ({tasksByStatus.length})
                    </h3>
                  </div>

                  {/* Task List */}
                  <div className="p-3 space-y-3">
                    {tasksByStatus.length === 0 ? (
                      <p className="text-xs text-center text-gray-400">
                        No tasks
                      </p>
                    ) : (
                      tasksByStatus.map((task) => (
                        <div
                          key={task.id}
                          className={`rounded-lg border p-3 text-sm transition hover:shadow-md ${
                            isOverdue(task.due_date)
                              ? "border-red-300 bg-red-50"
                              : "border-slate-200 bg-white"
                          }`}
                        >
                          {/* Title */}
                          <p className="font-medium text-slate-800">
                            {task.title}
                          </p>

                          {/* Project */}
                          <p className="text-xs text-slate-500 mt-1">
                            Project: {task.project_name || "-"}
                          </p>

                          {/* Meta */}
                          <div className="flex items-center justify-between mt-3 text-xs">
                            {/* Due date */}
                            <span
                              className={`flex items-center gap-1 ${
                                isOverdue(task.due_date)
                                  ? "text-red-600 font-semibold"
                                  : "text-slate-500"
                              }`}
                            >
                              <Clock size={12} />
                              {task.due_date
                                ? task.due_date.split("T")[0]
                                : "No due date"}
                            </span>

                            {/* Priority */}
                            <span
                              className={`px-2 py-0.5 rounded-full font-medium ${
                                priorityColor[task.priority || "medium"]
                              }`}
                            >
                              {(task.priority || "medium").toUpperCase()}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </EmployeeLayout>
  );
}
